import {
  Button,
  Center,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Text,
} from '@chakra-ui/react';

import {
  ExclamationCircleOutline,
  CloseCircleOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import { PauseReason } from 'app/constants/individual';

import useFetchIndividualPauses from 'app/hooks/useFetchIndividualPauses';
import useUnpauseIndividual from 'app/hooks/useUnpauseIndividual';

import { ReactivateEmployeeModalProps } from './types';

function ReactivateEmployeeModal({
  id,
  isOpen,
  onClose,
  onReactivate,
}: ReactivateEmployeeModalProps) {
  const toast = useToast();

  const { data: employeePauses, isLoading: isLoadingEmployeePauses } = useFetchIndividualPauses(
    [id],
    isOpen
  );

  const { mutate: reactivateEmployee, isLoading: isReactivatingEmployee } = useUnpauseIndividual({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Employee account reactivated',
        description: 'The employee was successfully reactivated!',
      });

      onReactivate();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the employee account was not reactivated. Please try again.',
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <ExclamationCircleOutline color="system.warning" boxSize="24px" />
            <Text as="h6">Reactivate account?</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close unpause employee"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          {isLoadingEmployeePauses ? (
            <Center width="full" height="full" flexGrow={1}>
              <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
            </Center>
          ) : (
            <Text color="brand.mutedPurple" textStyle="body">
              {`The account of this employee was paused due to "${
                PauseReason[employeePauses?.list[0].reason as keyof typeof PauseReason]
              }". Do you want to
            reactivate it?`}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            isLoading={isReactivatingEmployee}
            onClick={() => reactivateEmployee({ id })}
          >
            Yes, reactivate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ReactivateEmployeeModal;
